import type { ImplementationActionStep } from '@whizz/api-model';
export const actionSteps = {
  homeLearning: true,
  learningGaps: true,
  emotionalAndSocialEngagement: true,
  fluencyAndRecallFacts: true,
  reasoningSkills: true,
  multiplication: true,
  progressAndEngagement: true,
} as const;

export type ActionGoalKey = keyof typeof actionSteps;

export function isActionGoalKey(key: string | null): key is ActionGoalKey {
  return null !== key && Object.prototype.hasOwnProperty.call(actionSteps, key);
}

export interface ActionGoal {
  id?: string;
  actionSteps?: ImplementationActionStep[];
  goal_key: string | null;
  cohort: { id: number | number[] | null; name: string | null };
  metrics: (string | null)[];
}

export interface CohortOptions {
  yearGroup: { id: number; name: string }[];
  class: { id: number; name: string }[];
  tag: { id: number; name: string }[];
}

// null means an unresolved selection; an empty array means the whole school.
export function resolveActionCohort(goal: ActionGoal, options: CohortOptions): string[] | null {
  const { name, id } = goal.cohort;
  if ('school' === name || (!name && null === id)) return [];
  const kind = 'group' === name ? 'class' : name;
  if ('class' !== kind && 'yearGroup' !== kind && 'tag' !== kind) return null;
  const ids = Array.isArray(id) ? id : null === id ? [] : [ id ];
  if (!ids.length) return null;
  const names = ids.map((value) => options[kind].find((item) => item.id === value)?.name);
  return names.every((value) => undefined !== value) ? names as string[] : null;
}

export function cohortLabel(goal: ActionGoal, options: CohortOptions, labels: { school: string; unresolved: string }): string {
  const names = resolveActionCohort(goal, options);
  if (null === names) return labels.unresolved;
  return names.length ? names.join(', ') : labels.school;
}

// The vision is a single free-text field; the design shows its opening as a larger lead line.
// Split at the first blank line, else after the first sentence.
// null when there's no sentence break (e.g. a single sentence, or a script without . ! ? terminators).
export function splitFirstSentence(text: string): { first: string; rest: string } | null {
  const match = text.trim().match(/^(.+?[.!?])\s+(\S[\s\S]*)$/);
  return match ? { first: match[1].trim(), rest: match[2].trim() } : null;
}

export function splitVision(vision: string): { lead: string; body: string } {
  const text = vision.trim();
  const paragraph = text.split(/\n\s*\n/);
  if (paragraph.length > 1) return { lead: paragraph[0].trim(), body: paragraph.slice(1).join('\n\n').trim() };
  const sentence = splitFirstSentence(text);
  return sentence ? { lead: sentence.first, body: sentence.rest } : { lead: text, body: '' };
}
